import React, { useCallback } from "react";
import { Autocomplete, TextField } from "@mui/material";

const EMPTY_OBJECT = {}
const renderOption = (props, option) => (
  <li {...props} key={option.value}>
    {option.label}
  </li>
)
const isOptionEqualToValue = (option, val) => {
  if (!val) return true;
  return option?.value === val?.value;
}

/**
 * @typedef {Object} AutocompleteOption
 * @property {string|number} value - Уникальный идентификатор опции.
 * @property {string} label - Отображаемый текст опции.
 * @property {any} [key] - Любые дополнительные скрытые поля (например, из meta.hidden).
 */

/**
 * Обертка над компонентом Autocomplete из Material-UI.
 * 
 * @param {Object} props - Пропсы компонента.
 * @param {string} props.placeHolder - Текст подсказки (placeholder) или лейбла для поля ввода.
 * @param {(newValue: AutocompleteOption | AutocompleteOption[] | null) => void} props.action - 
 *   Функция-обработчик изменения значения (аналог onChange в MUI). 
 *   Принимает новый выбранный объект (или null при очистке) и объект события.
 * @param {AutocompleteOption[]} props.options - Массив опций для выпадающего списка.
 * @param {AutocompleteOption | null} [props.value] - Текущее выбранное значение. 
 *   Должно соответствовать структуре объектов из массива `options` или быть null.
 * @param {import('@mui/material').TextFieldProps} [props.textFieldProps] - 
 *   Пропсы, которые будут переданы непосредственно во внутренний компонент TextField (через renderInput).
 * @param {Omit<import('@mui/material').AutocompleteProps<AutocompleteOption, boolean, boolean, boolean>, 'options' | 'value' | 'onChange' | 'renderInput'>} [props.autocompleteProps] - 
 *   Дополнительные пропсы для MUI Autocomplete (например: disabled, loading, size, disableClearable и т.д.).
 * 
 * @returns {React.ReactElement} Отрендеренный компонент Autocomplete.
 * 
 * @example
 * <AutocompliteMUI
 *   placeHolder="Выберите город"
 *   options={citiesOptions}
 *   value={selectedCity}
 *   action={(newValue) => setSelectedCity(newValue)}
 *   textFieldProps={{ variant: "outlined", size: "small" }}
 *   autocompleteProps={{ 
 *     loading: isLoading,
 *     disabled: isDisabled,
 *     disableClearable: true,
 *     getOptionLabel: (option) => option.label.toUpperCase()
 *   }}
 * />
 */
export default function AutocompliteMUI({
  placeHolder,
  action,
  options,
  value,
  textFieldProps = EMPTY_OBJECT,
  autocompleteProps = EMPTY_OBJECT
}) {

  const {
    options: _ignoredOptions,
    value: _ignoredValue,
    onChange: _ignoredOnChange,
    renderInput: _ignoredRenderInput,
    renderOption: _renderOption,
    ...safeAutocompleteProps
  } = autocompleteProps;
  const onChange = useCallback((event, newValue) => action(newValue), [action])
  const renderInput = useCallback((params) => {
    return (
      <TextField
        {...params}
        variant="standard"
        label={textFieldProps?.label !== undefined ? textFieldProps.label : placeHolder}
        InputLabelProps={{
          shrink: true,
          ...textFieldProps.InputLabelProps,
        }}
        {...textFieldProps}
      />
    );
  }, [placeHolder, textFieldProps])
  return (
    <Autocomplete
      options={options}
      value={value || null}
      onChange={onChange}
      isOptionEqualToValue={isOptionEqualToValue}
      renderOption={renderOption}
      renderInput={renderInput}
      {...safeAutocompleteProps}
    />
  );
};